import { Location } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import {DestroyRef, inject, Injectable} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { ToastaService } from 'ngx-toasta';
import { combineLatestWith, forkJoin, Observable, of, OperatorFunction, pipe, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../../../../../entry-routes/authentication/authentication.service';
import { ApplicationConfigVarNames } from '../../../../../entry-routes/configuration/application-config-var-names';
import { ConfigurationService } from '../../../../../entry-routes/configuration/configuration.service';
import { ActivityDiff, CdxActivity } from '../../../../models/cdx-activity';
import { CdxAttachment } from '../../../../models/cdx-attachment';
import { CdxComment, CdxReply } from '../../../../models/cdx-comment';
import { CdxFile } from '../../../../models/cdx-file';
import { Assignation, CdxTask } from '../../../../models/cdx-task';
import { EsPage } from '../../../../models/es-page';
import { InternalRoutes } from '../../../../models/internal-routes';
import { Metadata } from '../../../../models/metadata';
import { Url } from '../../../../models/url';
import { UserSocketHttpResponse } from '../../../../models/user-socket-http-response';
import { SocketService } from '../../../../services/socket/socket.service';
import { Utils } from '../../../../utils/utils';
import { CriticalDataAction } from '../../../critical-data/action/critical-data.action';
import { CriticalDataService } from '../../../critical-data/service/critical-data.service';
import { CurrentCommentService } from '../../../current-comment/service/current-comment.service';
import { TaskSearchResultService } from '../../../search-result/service/task-search-result.service';
import { TaskFormService } from '../../../task-form/service/task-form.service';
import { TaskDetailsAction } from '../../action/task/task-details.action';
import { selectDetailsTaskDatasDetails, selectDetailsTaskFeature } from '../../details-selectors';
import { TaskDetailsState } from '../../reducer/task/task-details.reducer';
import { AbstractDetailsService, DELETE_MODE } from '../abstract-details.service';
import { WkfTaskService } from '../../../../services/workflow/wkf-task-service';
import { WkfTaskAttachmentService } from '../../../../services/workflow/wkf-task-attachment-service';
import {TaskDetails} from '../../../../models/task-details';
import {TaskResponse} from '../../../../models/workflow/taskResponse';
import {HttpErrorInterceptor} from '../../../../../entry-routes/http-error-interceptor/http-error-interceptor';
import { WkfLinkedProcessus } from '../../../../models/wkf-linked-processus';
import { TaskBaseInfosResponse } from '../../../../models/workflow/taskBaseInfosResponse';
import {Lock, LockInformation, LockType} from '../../../../models/lock';
import { ShareFileService } from '../../../../services/azure/share-file/share-file.service';
import { BlobFile } from '../../../../models/blob-file';
import { ShareLinkData } from '../../../../models/share-link-data';
import { AuthenticationResult } from '@azure/msal-common';
import { SignaturePayload } from '../../../../models/signature-payload';
import {
    DataResponseHistoricTaskInstanceResponse
} from '../../../../models/workflow/dataResponseHistoricTaskInstanceResponse';
import { WkfHistoryTaskService } from '../../../../services/workflow/wkf-history-task-service';
import {TaskContactTypeList} from '../../../../models/workflow/taskContact';
import {Entity} from '../../../../models/Entity';

@Injectable({
  providedIn: 'root'
})
export class TaskDetailsService extends AbstractDetailsService {

  private static colors = new Map<string, string>();

  private taskDetailsState$ = this.store.select(selectDetailsTaskFeature).pipe(takeUntilDestroyed());
  private taskDetails$ = this.store.select(selectDetailsTaskDatasDetails).pipe(takeUntilDestroyed());
  private taskDetails: CdxTask;
  private taskDetailsState: TaskDetailsState;
  private taskSearchResultService: TaskSearchResultService;
  private toggleSidenavTaskSubject = new Subject<any>();
  toggleSidenav$: Observable<any> = this.toggleSidenavTaskSubject.asObservable().pipe(takeUntilDestroyed());

  private destroyRef = inject(DestroyRef);

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type':  'application/json'
    })
  };

  constructor(
    httpClient: HttpClient,
    configAction: ConfigurationService,
    toastaService: ToastaService,
    translateService: TranslateService,
    socketService: SocketService,
    authenticationService: AuthenticationService,
    location: Location,
    router: Router,
    criticalDataService: CriticalDataService,
    currentCommentService: CurrentCommentService,
    private taskFormService: TaskFormService,
    private taskDetailsAction: TaskDetailsAction,
    private wkfTaskService: WkfTaskService,
    private wkfTaskAttachmentService: WkfTaskAttachmentService,
    private store: Store,
    protected shareFileService: ShareFileService,
    private wkfHistoryTaskService: WkfHistoryTaskService
  ) {
    super(httpClient, configAction, toastaService, translateService, socketService, authenticationService, location, router, criticalDataService, currentCommentService, shareFileService);
    this.taskDetails$.subscribe(details => this.taskDetails = details);
    this.taskDetailsState$.subscribe(details => this.taskDetailsState = details);
  }

  static getDocumentTypeColor(typeDoc: string): string {
    if (this.colors.get(typeDoc) != null) {
      return this.colors.get(typeDoc);
    }
    let hash = 0;
    for (let i = 0; i < typeDoc.length; i++) {
      hash = typeDoc.charCodeAt(i) + ((hash << 5) - hash);
    }
    let c = (hash & 0x00FFFFFF)
      .toString(16)
      .toUpperCase();

    c = '00000'.substring(0, 6 - c.length) + c;
    this.colors.set(typeDoc, '#' + c);
    return this.colors.get(typeDoc);
  }

  /*START COMMON*/
  public loadTaskDetailsVersion(id: string, versionNumber: number): void {
    try {
      this.taskDetailsAction.loadNexiaObjectDetailsStart();
      super._loadNexiaObjectDetailsVersion(id, versionNumber)
        .subscribe((task: CdxTask) => {
          this.taskDetailsAction.loadNexiaObjectDetailsSucceeded(task, true);
        }, (error) => {
          this.taskDetailsAction.loadNexiaObjectDetailsFailed(error);
        });
    } catch (error) {
      this.taskDetailsAction.loadNexiaObjectDetailsFailed(error);
    }
  }

  public loadReadCriticalFieldValue(id: string, fieldcode: string, versionNumber: number = null): void {
    console.log('loadReadCriticalFieldValue of : id :' + id + ' fieldcode :' + fieldcode);
    try {
      const updateDetails: CdxTask = _.cloneDeep(this.taskDetails);
      // traitement de vérification d'existence debut
      if (fieldcode !== CriticalDataAction._ALL && !!updateDetails.cdx_datas[fieldcode]) {
        // champ déjà récupéré
        this._addVisibleCriticalField(fieldcode);
        return;
      }
      // traitement de vérification d'existence fin
      this.taskDetailsAction.loadNexiaObjectDetailsStart();
      this._loadReadCriticalFieldValue(id, fieldcode, versionNumber).subscribe(
        (task) => {
          if (fieldcode !== CriticalDataAction._ALL) {
            updateDetails.cdx_datas[fieldcode] = task.cdx_datas[fieldcode];
            this._addVisibleCriticalField(fieldcode);
          } else {
            updateDetails.cdx_datas = task.cdx_datas;
            this.showAllCriticalFieldsValue();
          }
          this.taskDetailsAction.loadNexiaObjectDetailsSucceeded(updateDetails);
        },
        (error: HttpErrorResponse) => this.taskDetailsAction.loadDetailsFailed(error));
    } catch (error) {
      this.taskDetailsAction.loadNexiaObjectDetailsFailed(error);
    }
  }

  public deleteTask(task: CdxTask, mode: DELETE_MODE = DELETE_MODE.SOFT): Observable<boolean> {

    return this._deleteNexiaObject(task.cdx_id, mode)
      .pipe(
        map(() => {
          // this.taskSearchResultService.deleteStoreDocument(task.cdx_id);
          this.location.back();
          return true;
        }),
        catchError(err => {
          console.error(err);
          return of(true);
        })
      );
  }

  public hardDeleteTask(task: CdxTask): Observable<boolean> {
    return this.deleteTask(task, DELETE_MODE.HARD);
  }

  protected _addComment(comment: CdxComment, token: string): Observable<UserSocketHttpResponse> {
    const headers = Utils.objectTokenHeader(token);
    return this.socketService.post(
      Url.getWkfProtectedApiBaseUrl(this.configAction) + this.getPostPutCommentUrl(comment), comment,
      {
        responseType: 'text',
        headers: headers,
        observe: 'response'
      });
  }

  public addComment(comment: CdxComment, cdxTask : CdxTask): Observable<boolean> {
    const booleanSubject$: Subject<boolean> = new Subject<boolean>();
    const token:string = cdxTask.token;
    this._addComment(comment, token)
      .subscribe((userSocketHttpResponse: UserSocketHttpResponse) => {
          this.updateToken(userSocketHttpResponse.httpResponse.headers, comment.cdx_doc_id);
          this.loadComment(comment.cdx_doc_id, userSocketHttpResponse.id);
          this.loadAssignation(cdxTask.cdx_wkf.cdx_process.task_instance_id);
          this.loadTaskHistory(cdxTask.cdx_wkf.cdx_process.task_instance_id)
          this.scrollToDisplayComment(userSocketHttpResponse.id);
          booleanSubject$.next(false);
          booleanSubject$.complete();
        },
        (error) => {
          console.error(error);
          this.toastaService.error(this.translateService.instant('TOASTER_MESSAGES.[TASK_DETAILS].ADD_COMMENT_FAILED'));
          booleanSubject$.next(true);
          booleanSubject$.complete();
        });
    return booleanSubject$.asObservable();
  }

  protected _updateComment(comment: CdxComment, token: string): Observable<UserSocketHttpResponse> {
    const headers = Utils.objectTokenHeader(token);
    return this.socketService.put(
      Url.getWkfProtectedApiBaseUrl(this.configAction) + this.getPostPutCommentUrl(comment), comment,
      {
        responseType: 'text',
        headers: headers,
        observe: 'response'
      });
  }

  public updateComment(comment: CdxComment, cdxTask : CdxTask): Observable<boolean> {
    const booleanSubject$: Subject<boolean> = new Subject<boolean>();
    const token:string = cdxTask.token;
    this._updateComment(comment, token)
      .subscribe((userSocketHttpResponse: UserSocketHttpResponse) => {
          this.updateToken(userSocketHttpResponse.httpResponse.headers, comment.cdx_doc_id);
          this.loadComment(comment.cdx_doc_id, userSocketHttpResponse.id);
          this.loadAssignation(cdxTask.cdx_wkf.cdx_process.task_instance_id);
          this.loadTaskHistory(cdxTask.cdx_wkf.cdx_process.task_instance_id)
          booleanSubject$.next(false);
          booleanSubject$.complete();
        },
        (error) => {
          console.error(error);
          this.toastaService.error(this.translateService.instant('TOASTER_MESSAGES.[TASK_DETAILS].UPDATE_COMMENT_FAILED'));
          booleanSubject$.next(true);
          booleanSubject$.complete();
        });
    return booleanSubject$.asObservable();
  }

  public loadComment(processInstanceId: string, comId: string): void {
    // super._loadComment(taskId, comId);
    try {
      this.detailsActionLoadCommentStart();
      this.httpClient.get<CdxComment>(Url.getWkfProtectedApiBaseUrl(this.configAction) + this.getLoadDeleteCommentUrl(processInstanceId, comId))
        .subscribe(comment =>
            this.detailsActionLoadCommentSucceeded(comment),
          (error: HttpErrorResponse) => this.detailsActionLoadCommentFailed(error));
    } catch (error) {
      this.detailsActionLoadCommentFailed(error);
    }
  }

  public loadAssignation(taskId: string): void {
    try {
      this.detailsActionLoadAssignationStart();
      this.httpClient.get<CdxTask>(Url.getWkfProtectedApiBaseUrl(this.configAction) + this.getNexiaObjectDetailsUrl(taskId))
        .subscribe(task =>
            this.detailsActionLoadAssignationSucceeded(task.cdx_wkf.assignation),
          (error: HttpErrorResponse) => this.detailsActionLoadAssignationFailed(error));
    } catch (error) {
      this.detailsActionLoadAssignationFailed(error);
    }
  }

  protected _deleteComment(comment: CdxComment, token: string): Observable<UserSocketHttpResponse> {
    const headers = Utils.objectTokenHeader(token);
    return this.socketService.delete(
      Url.getWkfProtectedApiBaseUrl(this.configAction) + this.getLoadDeleteCommentUrl(comment.cdx_doc_id, comment.cdx_id),
      {
        observe: 'response',
        headers: headers
      });
  }

  public deleteComment(comment: CdxComment, cdxTask: CdxTask): void {
    const token:string = cdxTask.token;
    this._deleteComment(comment, token)
      .subscribe((userSocketHttpResponse: UserSocketHttpResponse) => {
        this.updateToken(userSocketHttpResponse.httpResponse.headers, comment.cdx_doc_id);
        this.loadComment(comment.cdx_doc_id, userSocketHttpResponse.id);
        this.loadAssignation(cdxTask.cdx_wkf.cdx_process.task_instance_id);
        this.loadTaskHistory(cdxTask.cdx_wkf.cdx_process.task_instance_id)
      });
  }

  protected _addReply(nexiaOnjectId: string, commentId: string, token: string, reply: CdxReply): Observable<UserSocketHttpResponse> {
    const headers = Utils.objectTokenHeader(token);
    return this.socketService.post(
      Url.getWkfProtectedApiBaseUrl(this.configAction) + this.getPostPutReplyUrl(nexiaOnjectId, commentId), reply,
      {
        responseType: 'text',
        headers: headers,
        observe: 'response'
      });
  }

  protected _updateReply(nexiaOnjectId: string, commentId: string, token: string, reply: CdxReply): Observable<UserSocketHttpResponse> {
    const headers = Utils.objectTokenHeader(token);
    return this.socketService.put(
      Url.getWkfProtectedApiBaseUrl(this.configAction) + this.getPostPutReplyUrl(nexiaOnjectId, commentId), reply,
      {
        responseType: 'text',
        headers: headers,
        observe: 'response'
      });
  }

  protected _deleteReply(nexiaOnjectId: string, commentId: string, token: string, replyId: string): Observable<UserSocketHttpResponse> {
    const headers = Utils.objectTokenHeader(token);
    return this.socketService.delete(
      Url.getWkfProtectedApiBaseUrl(this.configAction) + this.getDeleteReplyUrl(nexiaOnjectId, commentId, replyId),
      {
        observe: 'response',
        headers: headers
      });
  }

  public addReply(processInstanceId: string, commentId: string, reply: CdxReply, cdxTask: CdxTask): Observable<boolean> {
    const booleanSubject$: Subject<boolean> = new Subject<boolean>();

    delete reply.originComment_cdx_id; // == commentId : donc plus besoin pour envoyé au back ...
    const token:string = cdxTask.token;
    this._addReply(processInstanceId, commentId, token, reply)
      .subscribe((userSocketHttpResponse: UserSocketHttpResponse) => {
          this.updateToken(userSocketHttpResponse.httpResponse.headers, processInstanceId);
          this.loadComment(processInstanceId, userSocketHttpResponse.id);
          this.loadAssignation(cdxTask.cdx_wkf.cdx_process.task_instance_id);
          this.loadTaskHistory(cdxTask.cdx_wkf.cdx_process.task_instance_id);
          booleanSubject$.next(false);
          booleanSubject$.complete();
        },
        (error) => {
          console.error(error);
          this.toastaService.error(this.translateService.instant('TOASTER_MESSAGES.[TASK_DETAILS].ADD_REPLY_FAILED'));
          booleanSubject$.next(true);
          booleanSubject$.complete();
        });
    return booleanSubject$.asObservable();
  }

  public updateReply(processInstanceId: string, commentId: string, reply: CdxReply, cdxTask: CdxTask): Observable<boolean> {
    const booleanSubject$: Subject<boolean> = new Subject<boolean>();
    const token:string = cdxTask.token;
    this._updateReply(processInstanceId, commentId, token, reply)
      .subscribe((userSocketHttpResponse: UserSocketHttpResponse) => {
          this.updateToken(userSocketHttpResponse.httpResponse.headers, processInstanceId);
          this.loadComment(processInstanceId, userSocketHttpResponse.id);
          this.loadAssignation(cdxTask.cdx_wkf.cdx_process.task_instance_id);
          this.loadTaskHistory(cdxTask.cdx_wkf.cdx_process.task_instance_id)
          booleanSubject$.next(false);
          booleanSubject$.complete();
        },
        (error) => {
          console.error(error);
          this.toastaService.error(this.translateService.instant('TOASTER_MESSAGES.[TASK_DETAILS].UPDATE_REPLY_FAILED'));
          booleanSubject$.next(true);
          booleanSubject$.complete();
        });
    return booleanSubject$.asObservable();
  }

  public deleteReply(processInstanceId: string, commentId: string, replyId: string, cdxTask: CdxTask): void {
    const token:string = cdxTask.token;
    this._deleteReply(processInstanceId, commentId, token, replyId)
      .subscribe((userSocketHttpResponse: UserSocketHttpResponse) => {
        this.updateToken(userSocketHttpResponse.httpResponse.headers, processInstanceId);
        this.loadComment(processInstanceId, userSocketHttpResponse.id);
        this.loadAssignation(cdxTask.cdx_wkf.cdx_process.task_instance_id);
        this.loadTaskHistory(cdxTask.cdx_wkf.cdx_process.task_instance_id)
      },);
  }

  protected _loadNexiaObjectHistory(nexiaObjectId: string): void {
    try {
      this.detailsActionLoadHistoryStart();
      this.httpClient.get<EsPage<CdxActivity>>(Url.getWkfProtectedApiBaseUrl(this.configAction) + this.getHistoryUrl(nexiaObjectId))
        .subscribe((activities: EsPage<CdxActivity>) =>
            this.detailsActionLoadHistorySucceeded(activities),
          (error: HttpErrorResponse) => this.detailsActionLoadHistoryFailed(error));
    } catch (error) {
      this.detailsActionLoadHistoryFailed(error);
    }
  }

  public loadAttachment(taskId: string, attId: string, tempAttachmentId: string = null): void {
    super._loadAttachment(taskId, attId, tempAttachmentId);
  }

  public deleteAttachment(attachment: CdxAttachment, token: string): void {
    this.taskDetailsAction.deleteAttachmentStart();
    super._deleteAttachment(attachment, token)
      .subscribe((userSocketHttpResponse: UserSocketHttpResponse) => {
          this.updateToken(userSocketHttpResponse.httpResponse.headers, attachment.cdx_doc_id);
          this.loadTaskHistory(attachment.cdx_doc_id);
          this.taskDetailsAction.deleteAttachmentSucceeded(attachment);
        },
        (error) => {
          this.taskDetailsAction.deleteAttachmentFailed(error);
        });
  }

  public downloadAttachment(taskId: string, attId: string, token: string, isThumbnail: boolean): Observable<CdxFile> {
    return super._downloadAttachment(taskId, attId, token, isThumbnail);
  }
  /*
  public uploadAttachment(taskId: string, file: File, token: string): void {
    super._uploadAttachment(taskId, file, token);
  }
  */

  public uploadAttachment(taskId: string, file: File, token: string): void {
    console.log(taskId)
    console.log(file)
    console.debug(token)
    if (file != null && taskId != null){
      /*
      const body = {
        "name": file.name,
        "file": file,
        "mimeType": file.type
      }
      */
/*
      const formData = new FormData();
      formData.append("name", file.name);
      formData.append("file", file);
      formData.append("mimeType", file.type);
      */

      this.wkfTaskAttachmentService.createAttachment(taskId, null, file, file.name, 'description file', file.type, null, true).subscribe(attachmentResponse =>{
        console.log(attachmentResponse)
      })
    }
  }

  public updateAttachment(attachment: CdxAttachment, token: string): void {
    super._updateAttachment(attachment, token);
  }

  public loadTaskHistory(taskId: string): void {
    this._loadNexiaObjectHistory(taskId);
  }

  public updateTaskTeams(task: CdxTask, teamIds: string[]): Observable<boolean> {
    const booleanSubject$: Subject<boolean> = new Subject<boolean>();
    this._updateTeams(task.cdx_id, task.token, teamIds)
      .subscribe(() => {
          // we load the document because it is possible that the rights have changed
          this.loadTask(task.cdx_id);
          booleanSubject$.next(false);
          booleanSubject$.complete();
        },
        (error) => {
          console.error(error);
          booleanSubject$.next(true);
          booleanSubject$.complete();
        });
    return booleanSubject$.asObservable();
  }

  /*END getting urls*/

  public loadCurrentActivityDiff(taskId: string, evtId: number, isJustOpen: boolean): void {
    console.log('loadCurrentActivityDiff of : task id : ' + taskId + 'event id :' + evtId + ' isJustOpen :' + isJustOpen);
    super._loadCurrentActivityDiff(taskId, evtId, isJustOpen);
  }

  public loadTask(id: string, reloadForm = true): void {
    try {
      console.debug('loadTask ... id : ',id);
      this.taskDetailsAction.loadDetailsStart();

      // Authentification aux API nexia-wkf-api par token à la palce de basic-auth
      const chunkHeaders = Utils.objectTokenHeader(this.authenticationService.getApplicationTokenFromSessionStorage());

      const urlTask = Url.getFlowableProtectedApiBaseUrl(this.configAction)+Url.WKF_TASK +id;

      const urlBundleTaskDetail = Url.getFlowableProtectedApiBaseUrl(this.configAction)+Url.WKF_NEXIA_API+'/'+Url.TASKS+id;

      const bundleTaskDetails$ = this.httpClient.get<TaskDetails>(urlBundleTaskDetail, {headers: chunkHeaders});
        //return les sous-tâches terminées et en cours
      const historicTaskInstanceResponse$ = this.wkfHistoryTaskService.listHistoricTaskInstances(id);

        forkJoin(this.httpClient.get<TaskResponse>(urlTask, {headers: chunkHeaders}),historicTaskInstanceResponse$)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          combineLatestWith(bundleTaskDetails$)
        )
        .subscribe({
          next:([ [taskResponse, dataResponseHistoricTaskInstanceResponse], bundleTaskDetails]: [[TaskResponse, DataResponseHistoricTaskInstanceResponse], TaskDetails]) =>{
            // FIXME remove add of token
            bundleTaskDetails.details.token = this.addMockedToken();
            bundleTaskDetails.taskResponse = taskResponse;
            console.debug('loadTask() ....subscribe dataResponseHistoricTaskInstanceResponse.data:',dataResponseHistoricTaskInstanceResponse.data);
            bundleTaskDetails.subTasks = dataResponseHistoricTaskInstanceResponse.data;
            console.debug('loadTask() ....subscribe subTasks:',bundleTaskDetails.subTasks);
            if (reloadForm) {
              this.taskFormService.getTaskForm(id);
            }
            // this.currentCommentService.removeCurrentComment();
            this.taskDetailsAction.loadDetailsSucceeded(bundleTaskDetails);
          },
          error:(error: HttpErrorResponse)  => {
            console.error('Error received from back when loading Task details ', error);
            this.taskDetailsAction.loadDetailsFailed(error);
          }
        });
    } catch (error) {
      this.taskDetailsAction.loadDetailsFailed(error);
    }
  }

  loadHistoryTask(id: string): void {
    try {
      this.taskDetailsAction.loadDetailsStart();

      // Authentification aux API nexia-wkf-api par token à la palce de basic-auth
      const chunkHeaders = Utils.objectTokenHeader(this.authenticationService.getApplicationTokenFromSessionStorage());

      const urlBundleProcessTaskDetail = Url.getFlowableProtectedApiBaseUrl(this.configAction)+Url.WKF_NEXIA_API+'/'+Url.WKF_PROCESS+id;

      const bundleProcessTaskDetails$ = this.httpClient.get<TaskDetails>(urlBundleProcessTaskDetail, {headers: chunkHeaders});
      bundleProcessTaskDetails$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next:( bundleTaskDetails:  TaskDetails) => {
            // FIXME remove add of token
            bundleTaskDetails.details.token = this.addMockedToken();

            this.taskFormService.getProcessLastForm(id);
            // this.currentCommentService.removeCurrentComment();
            this.taskDetailsAction.loadDetailsSucceeded(bundleTaskDetails);
          },
          error:(error: HttpErrorResponse)  => {
            console.error('Error received from back when loading Task details ', error);
            this.taskDetailsAction.loadDetailsFailed(error);
          }
        });
    } catch (error) {
      this.taskDetailsAction.loadDetailsFailed(error);
    }
  }

  public setTaskSearchResultService(value: TaskSearchResultService) {
    this.taskSearchResultService = value;
  }

  public remove() {
    this.taskDetailsAction.removeAll();
  }

  public removeAttachmentOfStore(taskId: string, attId: string): void {
    this.taskDetailsAction.deleteAttachmentStart();
    const attachment: CdxAttachment = new CdxAttachment();
    attachment.cdx_id = attId;
    attachment.cdx_doc_id = taskId;
    this.taskDetailsAction.deleteAttachmentSucceeded(attachment);
  }

  public downloadTaskFile(task: CdxTask, path: string, readCritical = false): Observable<CdxFile> {
    try {
      return AbstractDetailsService.query()
        .pipe(
          this.readCriticalParams(readCritical),
          this.getTaskFile(task, path),
          map((response: HttpResponse<any>) => {
            this.updateToken(response.headers, task.cdx_id);
            return Utils.httpResponseToCdxFile(response);
          }, (error: HttpErrorResponse) => {
            console.error(error);
            return of(error);
          }));
    } catch (err) {
      console.error(err);
      return of(err);
    }
  }

  public downloadTaskFormat(task: CdxTask, formatName: string, formatFileId: string, readCritical = false): Observable<CdxFile> {
    try {
      return AbstractDetailsService.query()
        .pipe(
          this.readCriticalParams(readCritical),
          this.getTaskFormatFile(task, formatName, formatFileId),
          map((response: HttpResponse<any>) => {
            this.updateToken(response.headers, task.cdx_id);
            return Utils.httpResponseToCdxFile(response);
          }, (error: HttpErrorResponse) => {
            console.error(error);
            return of(error);
          }));
    } catch (err) {
      console.error(err);
      return of(err);
    }
  }

    /**
   * Récupérer le pdf depuis nexia-api/tasks/{taskId}/view
   */
  public getTaskFlowablePdf(currentTaskId: string, fromButton: boolean): Observable<HttpResponse<any>> {
    try {
      let urlGetPdf = Url.getFlowableProtectedApiBaseUrl(this.configAction) + Url.WKF_NEXIA_API + '/' + Url.TASKS + currentTaskId + '/view';
      if (fromButton) {
        urlGetPdf = urlGetPdf + '?readCritical';
      }

      return this.httpClient.get(
        urlGetPdf,
        {
          observe: 'response', responseType: 'arraybuffer'
        })
        .pipe(map((response: HttpResponse<any>) => {
          return response;
        }, (error: HttpErrorResponse) => {
          console.error(error);
          return of(error);
        }));
    } catch (err) {
      console.error(err);
      return of(err);
    }
  }

  public getAttachmentPdf(task: CdxTask, attachment: CdxAttachment): Observable<HttpResponse<any>> {
    try {
      const taskInstanceId = task.cdx_wkf.cdx_process.task_instance_id;
      const urlGetPdf = Url.getFlowableProtectedApiBaseUrl(this.configAction) + Url.WKF_NEXIA_API + '/' + Url.TASKS + taskInstanceId + '/'+ Url.ATTACHMENT + attachment.cdx_id + '/' + Url.DOCUMENT_PDF;


      return this.httpClient.get(
        urlGetPdf,
        {
          observe: 'response', responseType: 'arraybuffer',
          headers: Utils.objectTokenHeader(task.token)
        })
        .pipe(map((response: HttpResponse<any>) => {
          this.updateToken(response.headers, task.cdx_id);
          return response;
        }, (error: HttpErrorResponse) => {
          console.error(error);
          return of(error);
        }));
    } catch (err) {
      console.error(err);
      return of(err);
    }
  }

  public getAttachmentThumb(task: CdxTask, attachment: CdxAttachment): Observable<HttpResponse<any>> {
    try {
      const taskInstanceId = task.cdx_wkf.cdx_process.task_instance_id;
      const urlGetPdf = Url.getFlowableProtectedApiBaseUrl(this.configAction) + Url.WKF_NEXIA_API + '/' + Url.TASKS + taskInstanceId + '/'+ Url.ATTACHMENT + attachment.cdx_id + '/' + Url.THUMB;


      return this.httpClient.get(
        urlGetPdf,
        {
          observe: 'response', responseType: 'arraybuffer',
          headers: Utils.objectTokenHeader(task.token)
        })
        .pipe(map((response: HttpResponse<any>) => {
          this.updateToken(response.headers, task.cdx_id);
          return response;
        }, (error: HttpErrorResponse) => {
          console.error(error);
          return of(error);
        }));
    } catch (err) {
      console.error(err);
      return of(err);
    }
  }

  public downloadThumb(task: CdxTask): Observable<File> {
    if (!task.cdx_file) {
      return of(null);
    }
    try {
      return this.httpClient.get(
        `${Url.getWkfProtectedApiBaseUrl(this.configAction)}${Url.TASKS}${task.cdx_wkf.cdx_process.task_instance_id}/${Url.THUMB}`,
        {
          observe: 'response', responseType: 'arraybuffer',
          headers: Utils.objectTokenHeader(task.token).set(HttpErrorInterceptor.BYPASS_HEADER, '')
        })
        .pipe(map((response: HttpResponse<any>) => {
          const file: File = Utils.createFile(response);
          return file;
        }, (error: HttpErrorResponse) => {
          console.error(error);
          return of(error);
        }));
    } catch (err) {
      console.error(err);
      return of(err);
    }
  }

  public showHideInfosBlock(isVisible: boolean): void {
    this.taskDetailsAction.showHideInfoBlockSucceeded(isVisible);
  }

  public claimTask(taskId: string): Observable<boolean> {
    this.taskDetailsAction.loadDetailsStart();
        // permet d'avoir tout de suite la variable loading à true dans le taskPageComponent ( abonnement au store taskDetail )
        // pour avoir tout de suite la barre de progression sans avoir à attendre l'appel à loadTask() apres l'assignation coté workflow
    const booleanSubject$: Subject<boolean> = new Subject<boolean>();

    /*
      // inutile maintenant
      const delegant = this.authenticationService.currentDelegant;
      let claimEmail ='';
      if (!delegant) {
          const user: UserCredentials = this.authenticationService.getUserCredentials();
          claimEmail = user.email;
      } else {
          claimEmail = delegant.email;
      }
    */

    const objectToClaimTask = {
      "action": "claim"
    };

    const completeTask$ : Observable<any> = this.wkfTaskService.executeTaskAction(taskId,objectToClaimTask);
    completeTask$.subscribe( {
        next:() => {
          this.loadTask(taskId, false);
          // this.toastaService.info(this.translateService.instant('TOASTER_MESSAGES.TASK_FORM.CLAIM_TASK_SUCCEEDED'));
          booleanSubject$.next(true);
          booleanSubject$.complete();
        },
        error:(error: HttpErrorResponse) => {
          console.error('task form failed to be completed', error);
          this.toastaService.error(this.translateService.instant('TOASTER_MESSAGES.TASK_FORM.CLAIM_TASK_FAILED'));
          booleanSubject$.next(false);
          booleanSubject$.complete();

          this.taskDetailsAction.loadDetailsFailed(error);
          // pour ne plus avoir l'affichage de barre de progression déclenché précédemment par l'appel à loadDetailsStart() en cas d'erreur
        }
      }
    );
    return booleanSubject$.asObservable();
  }

  updateObservers(task: CdxTask, adds: number[], removes: number[]): void {
    const objectToUpdateObservers = {
      "type": "participant_observer",
      "add": adds,
      "remove": removes
    };
    const headers = Utils.objectTokenHeader(task.token);
    this.socketService.post(
      Url.getWkfProtectedApiBaseUrl(this.configAction) + this.getPostObserversUrl(task.cdx_wkf.cdx_process.task_instance_id), objectToUpdateObservers,
      {
        responseType: 'text',
        headers: headers,
        observe: 'response'
      })
      .subscribe({
        next: () => {
          //this.loadTask(task.cdx_wkf.cdx_process.task_instance_id);
         this.loadAssignation(task.cdx_wkf.cdx_process.task_instance_id);
        },
        error: err => {
          console.error(err);
        }
      });
  }

  /*START getting urls*/
  protected getProtectedApiBaseUrl(): string {
    return Url.getWkfProtectedApiBaseUrl(this.configAction);
  }

  protected getNexiaObjectDetailsUrl(taskId: string): string {
    return Url.TASKS + taskId + '/' + Url.DETAILS;
  }

  protected getInternalRouteDetailsUrl(taskId: string): string[] {
    return ['/' + InternalRoutes.WORKFLOW + '/' + InternalRoutes.TASKS, taskId, InternalRoutes.DETAILS];
  }

  protected getPostPutCommentUrl(comment: CdxComment): string {
    return Url.TASKS + comment.cdx_doc_id + '/' + Url.COMMENTS;
  }

  protected getLoadDeleteCommentUrl(processInstanceId: string, comId: string): string {
    return Url.TASKS + processInstanceId + '/' + Url.COMMENTS + comId;
  }

  protected getPostObserversUrl(taskId: string): string {
    return Url.TASKS + taskId + '/' + Url.PARTICIPANTS;
  }

  protected getPostQuestionToUrl(taskId: string): string {
    return Url.TASKS + taskId + '/' + Url.QUESTION_TO;
  }

  /*END calls to details actions*/

  protected getPostPutReplyUrl(taskId: string, comId: string): string {
    return Url.TASKS + taskId + '/' + Url.COMMENTS + comId + '/' + Url.REPLY;
  }

  protected getDeleteReplyUrl(taskId: string, comId: string, replyId: string): string {
    return Url.TASKS + taskId + '/' + Url.COMMENTS + comId + '/' + Url.REPLY + replyId + '/delete';
  }

  protected getHistoryUrl(taskId: string): string {
    return Url.TASKS + taskId + '/' + Url.HISTORY;
  }

  protected getLoadDeleteDownloadAttachmentUrl(taskId: string, attachmentId: string): string {
    return Url.TASKS + taskId + '/' + Url.ATTACHMENT + attachmentId;
  }

  protected getUpdateUploadAttachmentUrl(taskId: string): string {
    return Url.TASKS + taskId + '/' + Url.ATTACHMENT;
  }

  protected getRestoreVersionUrl(taskId: string, numVersion: string): string {
    return Url.TASKS + taskId + '/' + Url.RESTORE_REVISION + numVersion;
  }

  protected getCurrentActivityDiffUrl(taskId: string, evtId: number): string {
    return Url.TASKS + taskId + '/' + Url.HISTORY_EVT_REV + evtId;
  }

  protected getUpdateTeamsUrl(taskId: string): string {
    return Url.TASKS + taskId + '/' + Url.TEAMS;
  }

  protected getDeleteNexiaObjectUrl(taskId: string): string {
    return Url.TASKS + taskId;
  }

  protected getHardDeleteNexiaObjectsUrl(): string {
    return Url.DOCUMENTS + Url.DELETE;
  }

  /**
   * Récupère le document sur office et l'envoi au back
   * @param docId
   * @param file
   * @protected
   */
  protected updateArchive(docId: string, fileToUpload: File): Observable<boolean> {
    console.debug("-------> TaskDetailsService updateArchive docId : ", docId);
    const updateDone$: Subject<boolean> = new Subject<boolean>();
    const currentTaskId = this.taskDetails.cdx_wkf.cdx_process.task_instance_id;
    const typeDocument = this.getTypeDocumentFromDescription();
    const formData: FormData = new FormData();
    formData.append('file', fileToUpload);
    formData.append('documentTypeCode', typeDocument);

    try {
      this._uploadFile(currentTaskId, formData, this.taskDetailsState?.datas?.token)
        .subscribe({
          next : () => {
            updateDone$.next(true);
            updateDone$.complete();
          },
          error : () => {
            updateDone$.error(false);
            updateDone$.complete();
          }
        });
    } catch (error) {
      updateDone$.next(false);
      updateDone$.complete();
    }
    return updateDone$.asObservable();
  }

  protected getUpdateArchiveUrl(): string {
    return Url.TASKS;
  }

  protected getMetadata(objectId: string): Observable<Metadata> {
    return this.httpClient.get<Metadata>(Url.getWkfProtectedApiBaseUrl(this.configAction) + this.getMetadataUrl(objectId));
  }

  protected getMetadataUrl(taskInstanceId: string): string {
    return Url.TASKS + taskInstanceId + '/' + Url.METADATA;
  }

  /*START calls to details actions*/
  protected detailsActionLoadCommentStart(): void {
    this.taskDetailsAction.loadCommentStart();
  }

  protected detailsActionLoadCommentSucceeded(comment: CdxComment): void {
    this.taskDetailsAction.loadCommentSucceeded(comment);
  }

  protected detailsActionLoadCommentFailed(error: any): void {
    this.taskDetailsAction.loadCommentFailed(error);
  }

  protected detailsActionLoadHistoryStart(): void {
    this.taskDetailsAction.loadHistoryStart();
  }

  protected detailsActionLoadHistorySucceeded(activities: EsPage<CdxActivity>): void {
    this.taskDetailsAction.loadHistorySucceeded(activities);
  }

  protected detailsActionLoadHistoryFailed(error: any): void {
    this.taskDetailsAction.loadHistoryFailed(error);
  }

  protected detailsActionLoadAttachmentStart(): void {
    this.taskDetailsAction.loadAttachmentStart();
  }

  protected detailsActionLoadAttachmentSucceeded(attachment: CdxAttachment, tempAttachmentId: string): void {
    tempAttachmentId ? this.taskDetailsAction.loadAttachmentSucceeded(attachment, tempAttachmentId) : this.taskDetailsAction.loadAttachmentSucceeded(attachment);
  }

  /*END COMMON*/

  protected detailsActionLoadAttachmentFailed(error: any): void {
    this.taskDetailsAction.loadAttachmentFailed(error);
  }

  protected detailsActionUploadAttachmentSucceeded(attachment: CdxAttachment): void {
    this.taskDetailsAction.uploadAttachmentSucceeded(attachment);
  }

  protected detailsActionLoadMetaStart(): void {
    this.taskDetailsAction.loadMetaStart();
  }

  protected detailsActionLoadMetaSucceeded(meta: Metadata): void {
    this.taskDetailsAction.loadMetaSucceeded(meta);
  }

  protected detailsActionLoadMetaFailed(error: any): void {
    this.taskDetailsAction.loadMetaFailed(error);
  }

    protected detailsActionLoadAssignationStart(): void {
    this.taskDetailsAction.loadAssignationStart();
  }

  protected detailsActionLoadAssignationSucceeded(assignation: Assignation): void {
    this.taskDetailsAction.loadAssignationSucceeded(assignation);
  }

  protected detailsActionLoadAssignationFailed(error: any): void {
    this.taskDetailsAction.loadAssignationFailed(error);
  }

  protected detailsActionLoadCurrentActivityDiffStart(): void {
    this.taskDetailsAction.loadCurrentActivityDiffStart();
  }

  protected detailsActionLoadCurrentActivityDiffSucceeded(currentActivityDiff: ActivityDiff): void {
    this.taskDetailsAction.loadCurrentActivityDiffSucceeded(currentActivityDiff);
  }

  protected detailsActionLoadCurrentActivityDiffFailed(error: any): void {
    this.taskDetailsAction.loadCurrentActivityDiffFailed(error);
  }

  protected searchResultServiceUpdateDocInPage(taskInstanceId: string, meta: Metadata): void {
    this.taskSearchResultService.updateTaskInSearchResultPage(taskInstanceId, meta);
  }

  private scrollToDisplayComment(commentid : string ) {
    // ugly comme dans comments.directive.ts : le commentaire a été ajouté coté back
    // mais le navigateur n'a pas encore eu le temps de créer le dom du nouveau commentaire,
    // donc tempo avant de scroller dessus
    setTimeout(()=> {
      let documentCommentItemDiv = null;

      // recup du div commentaire cible ( on se sert de l'id pour le sélectionner)
      if(!!commentid ){
        documentCommentItemDiv = document.querySelector('#diplayAreaComment_'+commentid);
      }

      // Scroll jusqu'à l'élément
      if(!!documentCommentItemDiv) {
        documentCommentItemDiv.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } , 500)
  }

  getWkfLinkedProcessusInstancesFromProcessInstanceId( processInstanceId: string ): Observable<HttpResponse<WkfLinkedProcessus[]>> {
    const params: HttpParams = new HttpParams().set('processInstanceId', processInstanceId);
   return this.httpClient.get<WkfLinkedProcessus[]>(
     Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.TASKS + Url.WKF_WKFLINKEDPROCESSUSINSTANCESFROMPROCESSINSTANCEID
     ,
     {
     observe :'response',
      params : params
    });
  }

  getProcessLastTask( processInstanceId: string) : Observable<HttpResponse<TaskBaseInfosResponse>> {
    const params: HttpParams = new HttpParams()
    return this.httpClient.get<TaskBaseInfosResponse>(
      Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.WKF_PROCESS + processInstanceId+ Url.WKF_LAST_TASK
      ,
      {
        observe :'response',
        params : params
      }
    );
  }

  /*START SPECIFIC*/
  protected updateToken(headers: HttpHeaders, id: string): void {
    const taskDetailsState: TaskDetailsState = this.taskDetailsState;
    if (!!taskDetailsState && !!taskDetailsState.datas && !!taskDetailsState.datas.details && taskDetailsState.datas.details.cdx_id === id) {
      this.taskDetailsAction.updateToken(headers);
    }
  }

  private getApiMockUrl(taskId: string): string { // TODO remove API mock
    console.log('taskId '+taskId);
    return `${this.configAction.getConfigVariable(ApplicationConfigVarNames.API_MOCK_URL)}/assets/mock/task/task.json`;
  }

  private getMockPdf(): string {
    return `${this.configAction.getConfigVariable(ApplicationConfigVarNames.API_MOCK_URL)}/assets/mock/images/Modele-de-facture.pdf`;
  }

  private readCriticalParams(readCritical: boolean): OperatorFunction<HttpParams, HttpParams> {
    return pipe(
      map((params: HttpParams) => {
        if (readCritical) {
          params = params.append('readCritical', 'true');
        }
        return params;
      })
    );
  }

  private getTaskFile(task: CdxTask, path: string): OperatorFunction<HttpParams, HttpResponse<any>> {
    return switchMap(params => {
      return this.httpClient.get(
        `${Url.getWkfProtectedApiBaseUrl(this.configAction)}${Url.TASKS}${task.cdx_wkf.cdx_process.task_instance_id}/${path}`,
        {
          params: params,
          observe: 'response', responseType: 'arraybuffer',
          headers: Utils.objectTokenHeader(task.token)
        });
    });
  }

  private getTaskFormatFile(task: CdxTask, formatName: string, formatFileId: string): OperatorFunction<HttpParams, HttpResponse<any>> {
    return switchMap(params => {
      return this.httpClient.get(
        `${Url.getProtectedApiBaseUrl(this.configAction)}${Url.TASKS}${task.cdx_id}/${Url.FILE}/${Url.FORMATS}${formatName}/${formatFileId}`,
        {
          params: params,
          observe: 'response', responseType: 'arraybuffer',
          headers: Utils.objectTokenHeader(task.token)
        });
    });
  }

  // FIXME to be removed
  private addMockedToken(): string {
    return 'eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJhZG1pbkBheGVzcy5mciIsImlkIjoxMDA0LCJmdW5jdGlvbnMiOlsiQURNSU5DT01NRU5UIiwiVVBEQVRFQ09NTUVOVCIsIkRFTEVURUNPTU1FTlQiLCJERUxFVEUiLCJSRUFEQ09NTUVOVCIsIklOREVYIiwiQ1JFQVRFQVRUQUNITUVOVCIsIlVQREFURUFUVEFDSE1FTlQiLCJSRUFEQVRUQUNITUVOVCIsIlJFQUQiLCJBRE1JTkFUVEFDSE1FTlQiLCJSRUFESElTVE9SWSIsIklOREVYRU5USVRZIiwiUkVBRENSSVRJQ0FMIiwiVVBEQVRFIiwiREVMRVRFQVRUQUNITUVOVCIsIkNSRUFURUNPTU1FTlQiXSwib2JqX2lkIjoieDVobHM0QUJwOTVhUmNzWTlIS2EiLCJleHRlcm5hbF9pZCI6Ing1aGxzNEFCcDk1YVJjc1k5SEthIiwib2JqX3ZlcnNpb24iOiIyIiwiY2R4X3R5cGUiOiJkdF9hdmlzX2RfZWNoZWFuY2VfbWVuc3VlbCIsIm9ial9wcml2YWN5IjoiUFVCTElDIiwib2JqX3R5cGUiOiJjZHhfZG9jdW1lbnQiLCJqdGkiOiI4MDE3OTMwNS0xODY4LTQxMzAtOTdlZS1kNTZlN2RhNjA2NWMiLCJpYXQiOjE2NTU5Njg2ODAsImV4cCI6MTcxOTEwMDgwMH0.ZsjhVJusP68sm1HPiT8qNevBm01lYvLS1BVjST1ogNw';
  }

  public toggleSidenavTask(currentSubTask) {
    this.toggleSidenavTaskSubject.next(currentSubTask);
  }

  /*END SPECIFIC*/

      /**
   * Récupérer l'archive associé à la tache depuis l'api nexia-api/tasks/{taskId}/file
   */

  public downloadArchiveTask(currentTaskId: string): Observable<{file:Blob, fileName:string}> {
    try {
      const urlGetFile = Url.getFlowableProtectedApiBaseUrl(this.configAction) + Url.WKF_NEXIA_API + '/' + Url.TASKS + currentTaskId + '/file';
      return this.httpClient.get(urlGetFile,
        {
          observe: 'response', responseType: 'arraybuffer'
        })
        .pipe(map((response: HttpResponse<any>) => {
          const file: File = Utils.createFile(response);
          const contentDisposition = response.headers.get('Content-Disposition') || '';
          const fileName = this.getFilenameFromContentDisposition(contentDisposition);

          return{
            file,
            fileName
          } ;
        }, (error: HttpErrorResponse) => {
          console.error(error);
          return of(error);
        }));
    } catch (err) {
      console.error(err);
      return of(err);
    }
  }

  getFilenameFromContentDisposition(contentDisposition: string): string | null {
    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match != null && match[1]) {
      return match[1].replace(/['"]/g, '');
    }
    return null;
  }


  public _uploadFile(currentTaskId: string, formData: FormData, token: string): Observable<UserSocketHttpResponse> {
    const headers = Utils.objectTokenHeader(token);
    delete headers['Content-Type'];

    return this.socketService.post(
      Url.getFlowableProtectedApiBaseUrl(this.configAction)+Url.WKF_NEXIA_API+'/tasks/'+currentTaskId+'/file/', formData,
     );
  }

  /**
   * Méthode permettant de récuprer les modèles des documents templates par région et type de document.
   * @param documentType
   * @param region
   * @param thesaurusCode
   * @returns
   */
  getWkfTemplateDocument( documentType: string, region: string,  thesaurusCode: string): Observable<HttpResponse<any>> {
    const params = new HttpParams()
    .set('documentType', documentType)
    .set('region', region)
    .set('thesaurusCode', thesaurusCode);

   return this.httpClient.get<any>(
     Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.WKF_NEXIA_API_TEMPLATES
     ,
     {
     observe :'response',
      params : params
    });
  }
  /**
   * Méthode permettant de générer une archive associée à la tâche à partir d'un modèle de template et des datas
   * @param currentTaskId
   * @param templateUuid
   * @param formData
   * @param token
   * @returns
   */
  public generateArchiveFromTemplate(currentTaskId: string, templateUuid:string, datas: any, token: string): Observable<UserSocketHttpResponse> {
    const headers = Utils.objectTokenHeader(token);

    return this.socketService.post(
      Url.getFlowableProtectedApiBaseUrl(this.configAction)+Url.WKF_NEXIA_API+'/tasks/'+currentTaskId+'/template/'+templateUuid, datas,
      {
        headers
      }
    );
  }

  checkoutArchiveToSharePoint( meta: Metadata, cdxTask: CdxTask ): Observable<boolean> {
    // LOCK FILE IN NEXIA
    if (!meta) {
      meta = new Metadata();
    }
    if (!meta.archive_lock) {
      meta.archive_lock = new Lock();
    }
    meta.archive_lock.locked = true;
    return this.updateMetaArchive(meta, AbstractDetailsService.LOCK_ARCHIVE, cdxTask.cdx_wkf.cdx_process.task_instance_id, cdxTask.token, false).pipe(
      switchMap((isUpdated: boolean) => {
        if (isUpdated) {
          return this.shareFileService.checkLoginAndUpsertShareFolder();
        } else {
          return of(null);
        }
      }),
      switchMap((shareFolderCreated: boolean | null) => {
        console.log(shareFolderCreated);
        if (Utils.notNullAndNotUndefined(shareFolderCreated)) {
          if (shareFolderCreated) {
            const isCritical = false; // pas de gestion d'archive critique ici
            return this.getOrigFileAsByteArray(cdxTask, Url.FILE, isCritical).pipe(
              catchError((error) => {
                console.error(error);
                meta.archive_lock.locked = false;
                meta.archive_lock.lock_information = null;
                return this.updateMetaArchive(meta, AbstractDetailsService.UNLOCK_ARCHIVE, cdxTask.cdx_wkf.cdx_process.task_instance_id).pipe(
                  switchMap(() => {
                    return of(null);
                  })
                );
              })
            );
          } else {
            return this.updateMetaArchive(meta, AbstractDetailsService.UNLOCK_ARCHIVE, cdxTask.cdx_wkf.cdx_process.task_instance_id).pipe(
              switchMap(() => {
                return of(null);
              })
            );
          }
        }
        return of(null);
      }),
      switchMap((origFile: BlobFile) => {
        if (origFile) {
          return this.shareFileService.addFileToDriveAndCreateShareLink(origFile).pipe(
            catchError((error) => {
              console.error(error);
              meta.archive_lock.locked = false;
              meta.archive_lock.lock_information = null;
              return this.updateMetaArchive(meta, AbstractDetailsService.UNLOCK_ARCHIVE, cdxTask.cdx_wkf.cdx_process.task_instance_id).pipe(
                switchMap(() => {
                  return of(null);
                })
              );
            })
          );
        }
        return of(null);
      }),
      switchMap((shareLinkData: ShareLinkData) => {
        if (!shareLinkData) {
          return of(false);
        }
        console.log('fileId', shareLinkData.fileId);
        console.log('shareLink', shareLinkData.shareLink);
        if (!meta.archive_lock.lock_information) {
          meta.archive_lock.lock_information = new LockInformation();
        }
        meta.archive_lock.lock_information.link = shareLinkData.shareLink;
        meta.archive_lock.lock_information.lock_type = LockType.OFFICE_365;
        meta.archive_lock.lock_information.office_id = shareLinkData.fileId;
        window.open(shareLinkData.shareLink, '_blank');
        return this.updateMetaArchive(meta, AbstractDetailsService.UPDATE_LOCK_ARCHIVE_INFO, cdxTask.cdx_wkf.cdx_process.task_instance_id);
      })
    );
  }

  public updateMetaArchive(meta: Metadata, action: string, taskInstanceId: string, token: string | null = null, updateLock = true): Observable<boolean> {
    return super._updateMetaArchive(meta, action, taskInstanceId, token, updateLock);
  }

  public getOrigFileAsByteArray(cdxTask: CdxTask, path: string, readCritical = false): Observable<BlobFile | null> {
    try {
      return AbstractDetailsService.query()
        .pipe(
          this.readCriticalParams(readCritical),
          this.getTaskFile(cdxTask, path),
          map((response: HttpResponse<any>) => {
            this.updateToken(response.headers, cdxTask.cdx_id);
            return Utils.httpResponseToBlobFile(response);
          }, (error: HttpErrorResponse) => {
            console.error(error);
            return of(null);
          })
        );
    } catch (e) {
      return of(null);
    }
  }

  public loadMetadata(objectId: string): Observable<boolean> {
    return this._loadMetadata(objectId);
  }

  // surcharge car par défaut c'est l'url de l'api de la ged qui est utilisé
  protected _lockArchive(headers: HttpHeaders, taskInstanceId: string): Observable<any> {
    return this.socketService.post(Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.TASKS + taskInstanceId + '/' + Url.LOCK + Url.FILE, null,
      {
        responseType: 'text',
        headers: headers,
        observe: 'response'
      });
  }
  // surcharge car par défaut c'est l'url de l'api de la ged qui est utilisé
  protected _unlockArchive(headers: HttpHeaders, taskInstanceId: string): Observable<any> {
    return this.socketService.delete(Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.TASKS + taskInstanceId + '/' + Url.LOCK + Url.FILE, {
      headers: headers,
      observe: 'response'
    });
  }

  // surcharge car par défaut c'est l'url de l'api de la ged qui est utilisé
  protected _updateLockInfo(lockInformation: LockInformation, headers: HttpHeaders, taskInstanceId: string): Observable<any> {
    return this.socketService.put(Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.TASKS + taskInstanceId + '/' + Url.LOCK + Url.FILE + Url.DETAILS,
      lockInformation,
      {
        responseType: 'text',
        headers: headers,
        observe: 'response'
      });
  }

  deleteDriveItemAndUnlockArchive( taskMeta: Metadata, task_instance_id: string ): Observable<boolean>  {
    return this.deleteDriveItem(taskMeta).pipe(
      catchError((error) => {
        console.log(error);
        return of(true);
      }),
      switchMap(() => {
        return this.updateMetaArchive(taskMeta, AbstractDetailsService.UNLOCK_ARCHIVE, task_instance_id);
      })
    );
  }

  public getTypeDocumentFromDescription(): string {
    let typeDocument = '';
    const configTypeDocValue =this.taskDetailsState?.datas?.taskResponse?.description;
    if(!!configTypeDocValue ) {
      const configTypeDocJson = JSON.parse(configTypeDocValue);
      if(configTypeDocJson?.archive?.documentType && configTypeDocJson?.archive?.documentType[0]?.type === 'value'){
        typeDocument = configTypeDocJson.archive.documentType[0].value;
      }
    }
    return typeDocument;
  }

  public checkOfficeLogin(): Observable<AuthenticationResult> {
    return this.shareFileService.checkOfficeLogin();
  }

  public generateWithKeyValues( taskInstanceId: string, signaturePayload: SignaturePayload, token: string): Observable<UserSocketHttpResponse> {
      const headers = Utils.objectTokenHeader(token);
      return this.socketService.post(
          Url.getFlowableProtectedApiBaseUrl(this.configAction)+Url.WKF_NEXIA_API+'/templates/'+taskInstanceId+'/generate', signaturePayload,
          {
              responseType: 'text',
              headers: headers,
              observe: 'response'
          });
  }

  public generateWithKeyValuesWithoutSocket(taskInstanceId: string, signaturePayload: SignaturePayload ): Observable<HttpResponse<string>> {
      const headers = new HttpHeaders();
      return this.httpClient.post(
          Url.getFlowableProtectedApiBaseUrl(this.configAction)+Url.WKF_NEXIA_API+'/templates/'+taskInstanceId+'/generate', signaturePayload,
          {
              responseType: 'text',
              headers: headers,
              observe: 'response'
          });
  }

  public getConfigTaskJson(taskDetails : TaskDetails) : any {
      let configTaskJson = null;
      const configTask =taskDetails?.taskResponse?.description;
      if(!!configTask ) {
          try {
              configTaskJson = JSON.parse(configTask);
          } catch (e) {
              console.error('Erreur lors du parse de la config (description dans le modeler) de la tâche, vérifiez votre paramétrage, erreur : ', e);
              console.error('Erreur lors du parse de la config (description dans le modeler) de la tâche, vérifiez votre paramétrage, le contenu : ', configTask);
          }
      }
      return configTaskJson;
  }

  getConfigTypeDocument(taskDetails: TaskDetails): string {
      let typeDocument = '';
      const configTaskJson = this.getConfigTaskJson(taskDetails);
      if(!!configTaskJson ) {
          if( !!configTaskJson?.archive?.documentType && configTaskJson?.archive?.documentType[0]?.type === 'value'){
              typeDocument = configTaskJson.archive.documentType[0].value;
          }
      }
      return typeDocument;
  }

  getConfigRegion( taskDetails: TaskDetails ) : any {
      let configRegion = null;
      const configTaskJson = this.getConfigTaskJson(taskDetails);
      if(!!configTaskJson ) {
          if( configTaskJson?.region?.type && configTaskJson?.region?.value && configTaskJson?.region?.root ) {
              configRegion = configTaskJson?.region;
          }
      }
      return configRegion
  }

  getContactTypeList(): Observable<TaskContactTypeList[]> {
    return this.httpClient.get<TaskContactTypeList[]>(Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.CONTACTS + Url.CONTACT_TYPE);
  }

  getContacts(type: TaskContactTypeList, query: string): Observable<EsPage<Entity>> {
    return this.httpClient.get<EsPage<Entity>>(Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.CONTACTS + type.entity_type, {
      params: new HttpParams().set('_pattern', query)
    });
  }

  getContact(code: string, id: string): Observable<Entity> {
    return this.httpClient.get<Entity>(Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.CONTACTS + code + '/' + id);
  }
}

